// The Wolf's pick for one hole, on a phone clipped to a cart.
//
// Wolf is the one game on this card where the MONEY DOES NOT FOLLOW FROM THE SCORES. Four
// gross numbers say nothing about who was partnered with whom, so a hole with no pick is
// skipped outright by the engine (`Hole N skipped: no Wolf pick`) and that hole's money
// quietly never happens. That is why an unrecorded pick is shown here as a problem in red
// rather than an empty state — nobody discovers a missing pick on their own.
//
// TWO DECISIONS, NOT ONE. Declaring BLIND happens before a ball is struck: the wolf takes on
// all three having seen nothing, for the biggest multiple on the card. Picking a partner
// happens after the drives, having watched them. They are minutes apart in real life and
// worth different money, so they are separated here by a rule and a heading rather than
// sitting as a fourth chip in a row of four. Blind additionally takes a second tap to
// confirm, because it is the one control on this screen where a fat thumb is expensive.
//
// WHAT A PICK MEANS is fixed by `HoleData['wolf']` and mirrored from the phone app's
// WolfPickSheet, because the same engine reads both:
//
//   partner   { wolfId, partnerId, lone: false, blind: false }   2 v 2, stake ×1
//   lone      { wolfId, partnerId: null, lone: true,  blind: false }   1 v 3, ×loneMult
//   blind     { wolfId, partnerId: null, lone: true,  blind: true  }   1 v 3, ×blindMult
//
// Blind is ALSO lone. The engine reads `pick.lone || pick.blind` as "going it alone" and
// then prefers `blindMult` over `loneMult`; writing blind without lone would still compute,
// but it would be a second dialect of the same record and the phone writes this one.
//
// The one deliberate divergence from the phone: tapping an already-chosen partner here does
// NOT un-pick the hole. On the phone that toggle is harmless; here a mis-tap on a name is
// how a hole silently stops paying. Clearing is its own explicit button.

import { useEffect, useState } from 'react';

/** Exactly `HoleData['wolf']`. Kept structural so this file pulls in no engine code. */
export type WolfPickValue = {
  wolfId: string;
  partnerId: string | null;
  lone: boolean;
  blind: boolean;
};

/** One registered Wolf game, resolved for the hole on screen. */
export type WolfPickBlock = {
  /** `round_games.id`. The pick itself is per hole, not per game — see ScorePage. */
  gameId: string;
  /** Whose hole it is: `participantIds[holeIdx % participantIds.length]`, the engine's rotation. */
  wolfId: string;
  wolfName: string;
  /** The other three, in the game's participant order. */
  others: Array<{ id: string; name: string }>;
  /** What is recorded for this hole, or null when nothing is — or it names a different wolf. */
  pick: WolfPickValue | null;
  loneMult: number;
  blindMult: number;
  /** Scores are going on this hole and there is still no pick. The hole is paying nobody. */
  outstanding: boolean;
  /** The write for this hole has not reached the server yet. */
  unsaved: boolean;
};

type Props = {
  block: WolfPickBlock;
  /** null clears the hole's pick. */
  onPick: (gameId: string, next: WolfPickValue | null) => void;
};

/** "2×" / "2.5×" — a multiplier, never a money figure. The stake lives in the game setup. */
const mult = (n: number): string => (Number.isInteger(n) ? `${n}×` : `${n.toFixed(1)}×`);

export default function WolfPick({ block, onPick }: Props) {
  const { gameId, wolfId, wolfName, others, pick, loneMult, blindMult, outstanding, unsaved } =
    block;

  // Blind is armed, not fired, on the first tap. Reset whenever the hole or the recorded
  // pick changes underneath — this component is reused as the group walks up the course.
  const [armBlind, setArmBlind] = useState(false);
  useEffect(() => {
    setArmBlind(false);
  }, [gameId, wolfId, pick?.partnerId, pick?.lone, pick?.blind]);

  const partnerId = pick && !pick.lone ? pick.partnerId : null;
  const lone = !!pick?.lone && !pick.blind;
  const blind = !!pick?.blind;

  const choose = (next: WolfPickValue | null) => {
    setArmBlind(false);
    onPick(gameId, next);
  };

  const partnerName = partnerId ? others.find((o) => o.id === partnerId)?.name ?? null : null;

  /** The recorded pick in one sentence, or null when there is nothing to say yet. */
  const standing = blind
    ? `${wolfName} declared BLIND — alone against the other three at ${mult(blindMult)}`
    : lone
      ? `${wolfName} is going it alone against the other three at ${mult(loneMult)}`
      : partnerName
        ? `${wolfName} and ${partnerName} against the other two`
        : null;

  return (
    <div className={outstanding ? 'wp wp-out' : 'wp'}>
      <style>{CSS}</style>

      <div className="wp-head">
        <span className="wp-kind">Wolf</span>
        {unsaved ? <span className="wp-unsaved"><span className="hc-dot" /> not saved yet</span> : null}
      </div>

      <div className="wp-who">
        <span className="wp-who-name">{wolfName}</span>
        <span className="wp-who-tail">has the wolf on this hole</span>
      </div>

      {outstanding ? (
        <p className="wp-alarm">
          No pick recorded. Scores are going on this hole and the engine will skip it &mdash;
          it pays nobody.
        </p>
      ) : null}

      {/* ── Before anyone tees off ─────────────────────────────────────────── */}
      <div className={blind ? 'wp-blind wp-blind-on' : 'wp-blind'}>
        <div className="wp-blind-head">Before anyone tees off</div>
        {blind ? (
          <>
            <div className="wp-blind-state">
              <span className="wp-blind-mark">BLIND</span>
              <span className="wp-blind-mult tnum">{mult(blindMult)}</span>
            </div>
            <button type="button" className="wp-undo" onClick={() => choose(null)}>
              Undo blind
            </button>
          </>
        ) : armBlind ? (
          <>
            <p className="wp-blind-why">
              {wolfName} takes on all three having seen no drives, at {mult(blindMult)} instead
              of {mult(loneMult)}. Only callable before the first tee shot.
            </p>
            <div className="wp-blind-confirm">
              <button
                type="button"
                className="wp-blind-go"
                onClick={() =>
                  choose({ wolfId, partnerId: null, lone: true, blind: true })
                }
              >
                Yes &mdash; declare blind
              </button>
              <button type="button" className="wp-blind-no" onClick={() => setArmBlind(false)}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <button type="button" className="wp-blind-open" onClick={() => setArmBlind(true)}>
            <span className="wp-blind-open-main">Blind Wolf</span>
            <span className="wp-blind-open-mult tnum">{mult(blindMult)}</span>
          </button>
        )}
      </div>

      {/* ── After the drives ───────────────────────────────────────────────── */}
      <div className="wp-after">
        <div className="wp-after-head">After the drives</div>

        <div className="wp-picks">
          {others.map((o) => {
            const on = partnerId === o.id;
            return (
              <button
                key={o.id}
                type="button"
                className={on ? 'wp-pick on' : 'wp-pick'}
                aria-pressed={on}
                disabled={blind}
                onClick={() =>
                  choose({ wolfId, partnerId: o.id, lone: false, blind: false })
                }
              >
                <span className="wp-pick-name">{o.name}</span>
                <span className="wp-pick-sub">as partner</span>
              </button>
            );
          })}

          <button
            type="button"
            className={lone ? 'wp-pick wp-lone on' : 'wp-pick wp-lone'}
            aria-pressed={lone}
            disabled={blind}
            onClick={() => choose({ wolfId, partnerId: null, lone: true, blind: false })}
          >
            <span className="wp-pick-name">Lone Wolf</span>
            <span className="wp-pick-sub tnum">{mult(loneMult)} &middot; 1 v 3</span>
          </button>
        </div>

        {blind ? (
          <p className="wp-locked">
            Blind is already called &mdash; there is no partner to pick. Undo it above to go
            back to an ordinary hole.
          </p>
        ) : null}
      </div>

      {standing ? (
        <div className="wp-standing">
          <span className="wp-standing-text">{standing}</span>
          {!blind ? (
            <button type="button" className="wp-clear" onClick={() => choose(null)}>
              Clear pick
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const CSS = `
/* Paper card interior, like the contest block: this sits inside HoleCard. */
.wp {
  margin-top: 12px; padding: 12px;
  border: 2px solid var(--brass); border-radius: 8px;
  background: rgba(201,162,39,.10);
}
/* A hole with scores and no pick is not a blank — it is money going nowhere. */
.wp-out { border-color: var(--danger); background: rgba(208,67,59,.10); }

.wp-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.wp-kind {
  font-size: 15px; font-weight: 800; text-transform: uppercase; letter-spacing: .6px;
}
.wp-unsaved {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600; color: var(--ink-dim);
}

.wp-who { margin-top: 2px; display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.wp-who-name { font-size: 22px; font-weight: 800; line-height: 1.15; }
.wp-who-tail { font-size: 13px; color: var(--ink-dim); font-weight: 600; }

.wp-alarm {
  margin: 10px 0 0; padding: 8px 10px; border-radius: 8px;
  background: rgba(208,67,59,.16); border: 1px solid var(--danger);
  font-size: 13px; font-weight: 600; line-height: 1.4; color: var(--ink);
}

/* ── The blind zone. Its own bordered box, hard against nothing else, because it is a
      different decision taken at a different moment for different money. ───────────── */
.wp-blind {
  margin-top: 12px; padding: 10px;
  border: 1px dashed var(--ink-dim); border-radius: 8px;
  background: rgba(28,43,34,.05);
}
.wp-blind-on { border-style: solid; border-color: var(--ink); background: rgba(28,43,34,.10); }
.wp-blind-head {
  font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .9px;
  color: var(--ink-dim);
}
.wp-blind-open {
  margin-top: 8px; width: 100%; min-height: 60px;
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 0 16px; background: transparent; color: var(--ink);
  border: 1px solid var(--ink); font-weight: 800;
}
.wp-blind-open-main { font-size: 17px; }
.wp-blind-open-mult { font-size: 17px; color: var(--ink-dim); }
.wp-blind-why { margin: 8px 0 0; font-size: 13px; line-height: 1.4; color: var(--ink-dim); }
.wp-blind-confirm { display: flex; gap: 8px; margin-top: 10px; }
.wp-blind-go { flex: 2 1 0; min-height: 60px; font-size: 16px; }
.wp-blind-no {
  flex: 1 1 0; min-height: 60px; font-size: 15px;
  background: transparent; color: var(--ink); border: 1px solid var(--paper-border);
}
.wp-blind-state { display: flex; align-items: center; gap: 10px; margin-top: 6px; }
.wp-blind-mark {
  font-size: 20px; font-weight: 800; letter-spacing: 2px;
  background: var(--ink); color: var(--brass); padding: 4px 10px; border-radius: 6px;
}
.wp-blind-mult { font-size: 20px; font-weight: 800; }
.wp-undo {
  margin-top: 10px; width: 100%; min-height: 48px;
  background: transparent; color: var(--ink-dim); border: 1px solid var(--paper-border);
  font-size: 14px; font-weight: 700;
}

/* ── The ordinary pick. Four big targets, two up. ─────────────────────────── */
.wp-after { margin-top: 14px; }
.wp-after-head {
  font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: .9px;
  color: var(--ink-dim);
}
.wp-picks {
  margin-top: 8px; display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
}
.wp-pick {
  display: flex; flex-direction: column; align-items: flex-start; justify-content: center;
  gap: 2px; min-height: 68px; padding: 8px 12px; text-align: left;
  background: var(--paper); color: var(--ink); border: 1px solid var(--paper-border);
  touch-action: manipulation; -webkit-tap-highlight-color: transparent;
}
.wp-pick.on { background: var(--ink); color: var(--paper); border-color: var(--ink); }
.wp-pick:disabled { opacity: .4; }
.wp-pick-name {
  font-size: 16px; font-weight: 800;
  max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.wp-pick-sub { font-size: 11px; font-weight: 700; opacity: .72; }
.wp-lone { border-color: var(--ink-dim); }
.wp-locked { margin: 8px 0 0; font-size: 12px; line-height: 1.4; color: var(--ink-dim); }

.wp-standing {
  margin-top: 12px; padding-top: 10px; border-top: 1px solid var(--paper-border);
  display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap;
}
.wp-standing-text { font-size: 14px; font-weight: 700; line-height: 1.35; flex: 1 1 180px; }
.wp-clear {
  flex: none; min-height: 44px; padding: 0 14px;
  background: transparent; color: var(--ink-dim); border: 1px solid var(--paper-border);
  font-size: 13px; font-weight: 700;
}
`;
